import { toast } from 'react-toastify';
import {
    isCurrentUserAdmin,
    login,
    signUp,
    runLogoutTimer,
    saveTokenInLocalStorage,
} from 'shared/api/services/AuthService';
import { isRoleSelectionError, resolveLoginErrorMessage, resolveRegisterErrorMessage, translateAuthMessage } from './authErrorMessage';
import { setCachedEventAdminPanelAccess } from '../features/events/event-access';
import { consumePostLoginRedirect } from 'shared/utils/postLoginRedirect';
import { clearLiveBoardActivity, consumeLiveBoardRedirect } from '../features/b2b-live-board/live-board-session';

export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
export const LOGIN_FAILED_ACTION = '[login action] failed login';
export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
export const LOGOUT_ACTION = '[Logout action] logout action';
export const NAVTOGGLE = 'NAVTOGGLE';
export const NAV_OPEN = 'NAV_OPEN';
export const NAV_CLOSE = 'NAV_CLOSE';

const normalizeLoginRole = (value) => {
    const normalized = String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/ı/g, 'i');
    if (normalized === 'admin') return 'admin';
    if (normalized === 'manager' || normalized === 'yonetici' || normalized === 'yönetici') return 'manager';
    if (normalized === 'kullanici' || normalized === 'kullanıcı' || normalized === 'user') return 'user';
    return 'user';
};

// Giris/kayit sonrasi yonlendirme her zaman { replace: true } ile yapiliyor:
// aksi halde '/login' tarayici gecmisinde kaliyor, kullanici geri tusuna
// basinca oturum acikken /login'e donuyor ve orada 404 goruyordu.
const resolveUserDashboardPath = () => {
    return '/home';
};

export function signupAction(payload, navigate) {
    return (dispatch) => {
        dispatch(loadingToggleAction(true));
        signUp(payload)
            .then((response) => {
                dispatch(loadingToggleAction(false));
                toast.success(translateAuthMessage('auth.register.success'));
                const normalized = saveTokenInLocalStorage(response.data, { loginRole: 'user', isAdmin: false });
                const expireDate = normalized.expireDate
                    ? new Date(normalized.expireDate)
                    : new Date(Date.now() + 60 * 60 * 1000);
                runLogoutTimer(dispatch, Math.max(expireDate.getTime() - Date.now(), 0), () => Logout(navigate));
                dispatch(loginConfirmedAction(normalized));
                navigate(resolveUserDashboardPath(), { replace: true });
            })
            .catch((error) => {
                dispatch(loadingToggleAction(false));
                const errorMessage = resolveRegisterErrorMessage(error);
                dispatch(signupFailedAction(errorMessage));
            });
    };
}

const _apiBaseUrl = (process.env.REACT_APP_API_BASE_URL ?? '').trim() || 'http://localhost:3000';

function _readTokens() {
    try {
        const raw = localStorage.getItem('userDetails');
        const refreshToken = raw ? JSON.parse(raw)?.refreshToken : null;
        const accessToken = localStorage.getItem('accessToken');
        return { refreshToken, accessToken };
    } catch {
        return { refreshToken: null, accessToken: null };
    }
}

function _clearLocalSession(navigate) {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('accessToken');
    // Bellekteki yetki cache'i temizlenmezse bir sonraki kullanici, sunucudan
    // kendi cevabi gelene kadar onceki oturumun yetkisiyle degerlendiriliyor.
    setCachedEventAdminPanelAccess(null);
    navigate('/login');
}

export function Logout(navigate) {
    const { refreshToken, accessToken } = _readTokens();
    if (refreshToken) {
        fetch(`${_apiBaseUrl}/auth/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
            body: JSON.stringify({ refreshToken }),
        }).catch(() => {});
    }
    _clearLocalSession(navigate);
    return { type: LOGOUT_ACTION };
}

export function LogoutDevice(navigate) {
    const { refreshToken, accessToken } = _readTokens();
    if (refreshToken) {
        fetch(`${_apiBaseUrl}/auth/logout/device`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
            body: JSON.stringify({ refreshToken }),
        }).catch(() => {});
    }
    _clearLocalSession(navigate);
    return { type: LOGOUT_ACTION };
}

export function loginAction(email, password, loginRoleOrNavigate, navigateMaybe) {
    const navigate = typeof loginRoleOrNavigate === 'function' ? loginRoleOrNavigate : navigateMaybe;
    const hasExplicitRole = typeof loginRoleOrNavigate !== 'function';
    const loginRole = normalizeLoginRole(hasExplicitRole ? loginRoleOrNavigate : 'user');
    const fallbackRoles = hasExplicitRole
        ? [loginRole]
        : ['user', 'admin', 'manager'];

    return async (dispatch) => {
        dispatch(loadingToggleAction(true));
        const roleSequence = fallbackRoles;
        let lastError = null;

        const tryNextRole = (index) => {
            if (index >= roleSequence.length) {
                const errorMessage = resolveLoginErrorMessage(lastError, loginRole);
                dispatch(loginFailedAction(errorMessage));
                return;
            }

            const roleToTry = roleSequence[index];
            return login({ email, password, loginRole: roleToTry })
                .then(async (response) => {
                    // Yetki sorgusu YENI token ile gitmeli: once oturum kaydedilir.
                    // Sorgu basarisiz olsa da giris basarilidir; yetki varsayilan kalir.
                    saveTokenInLocalStorage(response.data, { loginRole: roleToTry, isAdmin: false });
                    const isAdmin = await isCurrentUserAdmin().catch(() => false);
                    const normalized = saveTokenInLocalStorage(response.data, { loginRole: roleToTry, isAdmin });
                    const expireDate = normalized.expireDate
                        ? new Date(normalized.expireDate)
                        : new Date(Date.now() + 60 * 60 * 1000);
                    runLogoutTimer(dispatch, Math.max(expireDate.getTime() - Date.now(), 0), () => Logout(navigate));
                    dispatch(loginConfirmedAction(normalized));
                    // B2B anlık takip ekranından login'e düşüldüyse önce oraya dönülür.
                    clearLiveBoardActivity();
                    const liveBoardRedirect = consumeLiveBoardRedirect();
                    const postLoginRedirect = consumePostLoginRedirect(resolveUserDashboardPath());
                    navigate(liveBoardRedirect || postLoginRedirect, { replace: true });
                })
                .catch((error) => {
                    lastError = error;
                    if (!hasExplicitRole && isRoleSelectionError(error)) {
                        return tryNextRole(index + 1);
                    }
                    const errorMessage = resolveLoginErrorMessage(error, roleToTry);
                    dispatch(loginFailedAction(errorMessage));
                });
        };

        return tryNextRole(0);
    };
}

export const loginFailedAction = (payload) => ({ type: LOGIN_FAILED_ACTION, payload });
export const loginConfirmedAction = (payload) => ({ type: LOGIN_CONFIRMED_ACTION, payload });
export const confirmedSignupAction = (payload) => ({ type: SIGNUP_CONFIRMED_ACTION, payload });
export const signupFailedAction = (payload) => ({ type: SIGNUP_FAILED_ACTION, payload });
export const loadingToggleAction = (payload) => ({ type: LOADING_TOGGLE_ACTION, payload });
export const navtoggle = () => ({ type: NAVTOGGLE });
export const navOpen = () => ({ type: NAV_OPEN });
export const navClose = () => ({ type: NAV_CLOSE });
